import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Avatar,
  Badge,
  Divider,
  Grid,
  GridItem,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Icon,
  useToast,
  Spinner,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiUser,
  FiBriefcase,
  FiFileText,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiStar,
  FiDownload,
  FiEye,
} from "react-icons/fi";
import { digitalFilesService } from "../digitalFilesService";

const EmployeeProfileModal = ({ isOpen, onClose, employee, onDataUpdate }) => {
  // Dark mode colors
  const bgColor = useColorModeValue("white", "gray.800");
  const cardBg = useColorModeValue("white", "gray.750");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const [profileData, setProfileData] = useState({
    personalInfo: null,
    jobHistory: [],
    documents: [],
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const toast = useToast();

  useEffect(() => {
    if (isOpen && employee) {
      fetchEmployeeProfile();
    }
  }, [isOpen, employee]);

  const fetchEmployeeProfile = async () => {
    if (!employee) return;

    try {
      setLoading(true);

      // Fetch all profile data in parallel
      const [personalInfo, jobHistory, documents] = await Promise.all([
        digitalFilesService.personalInfo
          .getPersonalInfo(employee.employeeId)
          .catch(() => null),
        digitalFilesService.jobHistory
          .getJobHistory(employee.employeeId)
          .catch(() => []),
        digitalFilesService.documents
          .getDocuments(employee.employeeId)
          .catch(() => []),
      ]);

      setProfileData({
        personalInfo: personalInfo?.data || null,
        jobHistory: Array.isArray(jobHistory?.data) ? jobHistory.data : [],
        documents: Array.isArray(documents?.data) ? documents.data : [],
      });
    } catch (error) {
      console.error("Error fetching employee profile:", error);
      toast({
        title: "Error loading profile",
        description: "Could not load complete employee profile",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (document) => {
    try {
      await digitalFilesService.documents.downloadDocument(document._id);
      toast({
        title: "Download started",
        status: "success",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const renderPersonalInfo = () => {
    const { personalInfo } = profileData;

    if (!personalInfo) {
      return (
        <Box textAlign="center" py={8}>
          <Text color={mutedColor}>No personal information available</Text>
        </Box>
      );
    }

    return (
      <VStack spacing={6} align="stretch">
        {/* Personal Details */}
        <Card bg={cardBg} borderColor={borderColor}>
          <CardBody>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Personal Details
            </Text>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={4}
            >
              <Box>
                <Text fontSize="sm" color={mutedColor}>
                  Full Name
                </Text>
                <Text fontWeight="medium">
                  {personalInfo.personalInfo?.firstName}{" "}
                  {personalInfo.personalInfo?.lastName}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color={mutedColor}>
                  Date of Birth
                </Text>
                <Text>
                  {personalInfo.personalInfo?.dateOfBirth
                    ? new Date(
                        personalInfo.personalInfo.dateOfBirth
                      ).toLocaleDateString()
                    : "Not specified"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color={mutedColor}>
                  Gender
                </Text>
                <Text>
                  {personalInfo.personalInfo?.gender || "Not specified"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color={mutedColor}>
                  Marital Status
                </Text>
                <Text>
                  {personalInfo.personalInfo?.maritalStatus || "Not specified"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color={mutedColor}>
                  Nationality
                </Text>
                <Text>
                  {personalInfo.personalInfo?.nationality || "Not specified"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color={mutedColor}>
                  Contact
                </Text>
                <VStack align="start" spacing={1}>
                  <HStack>
                    <FiMail size={14} />
                    <Text fontSize="sm">
                      {personalInfo.personalInfo?.email}
                    </Text>
                  </HStack>
                  <HStack>
                    <FiPhone size={14} />
                    <Text fontSize="sm">
                      {personalInfo.personalInfo?.phone}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </Grid>
          </CardBody>
        </Card>

        {/* Address */}
        <Card bg={cardBg} borderColor={borderColor}>
          <CardBody>
            <Text fontSize="lg" fontWeight="bold" mb={4}>
              Address Information
            </Text>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={6}
            >
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Permanent Address
                </Text>
                <VStack align="start" spacing={1}>
                  <Text>
                    {personalInfo.address?.permanent?.street || "Not specified"}
                  </Text>
                  <Text fontSize="sm" color={mutedColor}>
                    {personalInfo.address?.permanent?.city},{" "}
                    {personalInfo.address?.permanent?.state}{" "}
                    {personalInfo.address?.permanent?.zipCode}
                  </Text>
                  <Text fontSize="sm" color={mutedColor}>
                    {personalInfo.address?.permanent?.country}
                  </Text>
                </VStack>
              </Box>
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Current Address
                </Text>
                <VStack align="start" spacing={1}>
                  <Text>
                    {personalInfo.address?.current?.street || "Not specified"}
                  </Text>
                  <Text fontSize="sm" color={mutedColor}>
                    {personalInfo.address?.current?.city},{" "}
                    {personalInfo.address?.current?.state}{" "}
                    {personalInfo.address?.current?.zipCode}
                  </Text>
                  <Text fontSize="sm" color={mutedColor}>
                    {personalInfo.address?.current?.country}
                  </Text>
                </VStack>
              </Box>
            </Grid>
          </CardBody>
        </Card>

        {/* Emergency Contacts */}
        {personalInfo.emergencyContacts?.length > 0 && (
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                Emergency Contacts
              </Text>
              <VStack spacing={3} align="stretch">
                {personalInfo.emergencyContacts.map((contact, index) => (
                  <Box
                    key={index}
                    p={3}
                    border="1px"
                    borderColor="gray.200"
                    borderRadius="md"
                  >
                    <Grid
                      templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                      gap={4}
                    >
                      <Box>
                        <Text fontSize="sm" color={mutedColor}>
                          Name
                        </Text>
                        <Text fontWeight="medium">{contact.name}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color={mutedColor}>
                          Relationship
                        </Text>
                        <Text>{contact.relationship}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color={mutedColor}>
                          Contact
                        </Text>
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm">{contact.phone}</Text>
                          <Text fontSize="sm">{contact.email}</Text>
                        </VStack>
                      </Box>
                    </Grid>
                  </Box>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Health Information */}
        {(personalInfo.healthInfo?.bloodGroup ||
          personalInfo.healthInfo?.allergies) && (
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                Health Information
              </Text>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <Box>
                  <Text fontSize="sm" color={mutedColor}>
                    Blood Group
                  </Text>
                  <Text>
                    {personalInfo.healthInfo?.bloodGroup || "Not specified"}
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color={mutedColor}>
                    Allergies
                  </Text>
                  <Text>
                    {personalInfo.healthInfo?.allergies || "None specified"}
                  </Text>
                </Box>
                {personalInfo.healthInfo?.medicalConditions && (
                  <Box gridColumn={{ base: "1", md: "span 2" }}>
                    <Text fontSize="sm" color={mutedColor}>
                      Medical Conditions
                    </Text>
                    <Text>{personalInfo.healthInfo.medicalConditions}</Text>
                  </Box>
                )}
              </Grid>
            </CardBody>
          </Card>
        )}
      </VStack>
    );
  };

  const renderJobHistory = () => {
    const { jobHistory } = profileData;

    if (jobHistory.length === 0) {
      return (
        <Box textAlign="center" py={8}>
          <Text color={mutedColor}>No job history available</Text>
        </Box>
      );
    }

    return (
      <VStack spacing={4} align="stretch">
        {jobHistory.map((job, index) => (
          <Card key={job._id || index}>
            <CardBody>
              <HStack justify="space-between" align="start" mb={3}>
                <Box>
                  <HStack flexWrap="wrap">
                    <Text fontSize="lg" fontWeight="bold">
                      {job.positionInfo?.jobTitle}
                    </Text>
                    {job.positionInfo?.isCurrentPosition && (
                      <Badge colorScheme="green">Current Position</Badge>
                    )}
                  </HStack>
                  <Text color={mutedColor}>{job.positionInfo?.department}</Text>
                </Box>
                <Box textAlign="right">
                  <Text fontSize="sm" color={mutedColor}>
                    {new Date(job.positionInfo?.startDate).toLocaleDateString()}{" "}
                    -
                    {job.positionInfo?.endDate
                      ? new Date(job.positionInfo?.endDate).toLocaleDateString()
                      : "Present"}
                  </Text>
                  <Text fontSize="sm">{job.positionInfo?.employmentType}</Text>
                </Box>
              </HStack>

              {job.compensation?.salary && (
                <Box mb={3}>
                  <Text fontSize="sm" color={mutedColor}>
                    Compensation
                  </Text>
                  <Text fontWeight="medium">
                    ${job.compensation.salary.toLocaleString()}{" "}
                    {job.compensation.currency} ({job.compensation.payFrequency}
                    )
                  </Text>
                </Box>
              )}

              {/* Performance Reviews */}
              {job.performanceReviews?.length > 0 && (
                <Box mb={3}>
                  <Text fontSize="sm" color={mutedColor} mb={2}>
                    Performance Reviews
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {job.performanceReviews.map((review, reviewIndex) => (
                      <Box
                        key={reviewIndex}
                        p={2}
                        bg="gray.50"
                        borderRadius="md"
                      >
                        <HStack justify="space-between" mb={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            {new Date(review.reviewDate).toLocaleDateString()}
                          </Text>
                          <HStack>
                            <Text fontSize="sm">{review.rating}/5</Text>
                            <HStack spacing={1}>
                              {[...Array(5)].map((_, i) => (
                                <FiStar
                                  key={i}
                                  color={i < review.rating ? "gold" : "gray"}
                                  size={12}
                                />
                              ))}
                            </HStack>
                          </HStack>
                        </HStack>
                        {review.comments && (
                          <Text fontSize="sm" color="gray.700">
                            {review.comments}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* Training Programs */}
              {job.trainingPrograms?.length > 0 && (
                <Box>
                  <Text fontSize="sm" color={mutedColor} mb={2}>
                    Training Programs
                  </Text>
                  <HStack spacing={2} wrap="wrap">
                    {job.trainingPrograms.map((training, trainingIndex) => (
                      <Badge
                        key={trainingIndex}
                        colorScheme="blue"
                        variant="subtle"
                      >
                        {training.programName}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              )}
            </CardBody>
          </Card>
        ))}
      </VStack>
    );
  };

  const renderDocuments = () => {
    const { documents } = profileData;

    if (documents.length === 0) {
      return (
        <Box textAlign="center" py={8}>
          <Text color={mutedColor}>No documents available</Text>
        </Box>
      );
    }

    return (
      <VStack spacing={3} align="stretch">
        {documents.map((document) => (
          <Card key={document._id}>
            <CardBody>
              <HStack
                justify="space-between"
                align="start"
                flexDirection={{ base: "column", md: "row" }}
                gap={3}
              >
                <HStack spacing={3} flex="1">
                  <Icon
                    as={FiFileText}
                    color="blue.500"
                    boxSize={5}
                    display={{ base: "none", md: "block" }}
                  />
                  <Box flex="1">
                    <Text fontWeight="medium">{document.documentName}</Text>
                    <Text fontSize="sm" color={mutedColor}>
                      {document.documentType} • {document.originalFileName}
                    </Text>
                    <HStack spacing={4} mt={1} flexWrap="wrap">
                      <Text fontSize="xs" color={mutedColor}>
                        Uploaded:{" "}
                        {new Date(document.createdAt).toLocaleDateString()}
                      </Text>
                      <Badge
                        size="sm"
                        colorScheme={
                          document.verificationStatus === "Verified"
                            ? "green"
                            : document.verificationStatus === "Rejected"
                            ? "red"
                            : "orange"
                        }
                      >
                        {document.verificationStatus}
                      </Badge>
                    </HStack>
                  </Box>
                </HStack>
                <Button
                  size="sm"
                  leftIcon={<FiDownload />}
                  onClick={() => handleDownloadDocument(document)}
                  minH="44px"
                  width={{ base: "100%", md: "auto" }}
                >
                  Download
                </Button>
              </HStack>
            </CardBody>
          </Card>
        ))}
      </VStack>
    );
  };

  if (loading) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={{ base: "full", md: "6xl" }}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent bg={bgColor}>
          <ModalBody>
            <Box textAlign="center" py={12}>
              <Spinner size="xl" color="blue.500" mb={4} />
              <Text>Loading employee profile...</Text>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "full", md: "6xl" }}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent bg={bgColor}>
        <ModalHeader px={{ base: 4, md: 6 }}>
          <HStack spacing={3} flexWrap="wrap">
            <Avatar name={employee?.name} size="md" />
            <Box>
              <Text fontSize="lg">{employee?.name}</Text>
              <Text fontSize="sm" color={mutedColor}>
                Employee ID: {employee?.employeeId}
              </Text>
            </Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: 4, md: 6 }}>
          <Tabs variant="enclosed" colorScheme="blue">
            <TabList
              overflowX="auto"
              css={{
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
              }}
            >
              <Tab minW={{ base: "auto", md: "unset" }}>
                <HStack spacing={2}>
                  <Icon as={FiUser} />
                  <Text display={{ base: "none", md: "block" }}>
                    Personal Info
                  </Text>
                  <Text display={{ base: "block", md: "none" }}>Info</Text>
                </HStack>
              </Tab>
              <Tab minW={{ base: "auto", md: "unset" }}>
                <HStack spacing={2}>
                  <Icon as={FiBriefcase} />
                  <Text display={{ base: "none", md: "block" }}>
                    Job History
                  </Text>
                  <Text display={{ base: "block", md: "none" }}>Jobs</Text>
                </HStack>
              </Tab>
              <Tab minW={{ base: "auto", md: "unset" }}>
                <HStack spacing={2}>
                  <Icon as={FiFileText} />
                  <Text>Documents</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={{ base: 0, md: 4 }} py={{ base: 4, md: 6 }}>
                {renderPersonalInfo()}
              </TabPanel>
              <TabPanel px={{ base: 0, md: 4 }} py={{ base: 4, md: 6 }}>
                {renderJobHistory()}
              </TabPanel>
              <TabPanel px={{ base: 0, md: 4 }} py={{ base: 4, md: 6 }}>
                {renderDocuments()}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter px={{ base: 4, md: 6 }}>
          <Button
            variant="ghost"
            onClick={onClose}
            minH="44px"
            width={{ base: "100%", md: "auto" }}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EmployeeProfileModal;
